import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IPreviewClaimResponseDTO,
  useCompleteClaimMutation,
  usePreviewClaimMutation,
} from "../../redux/api/claim/claimAPI";
import LoginLayout from "../../shared/oversight-core/shared-components/login-layout/login-layout";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import { IHttpError } from "../../shared/oversight-core/interfaces/http-errror";
import AppButton from "../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppInput from "../../shared/oversight-core/ui-elements/input/app-input";
import { passwordRegex } from "../../shared/oversight-core/utils/regex";
import { showErrorMessage } from "../../shared/oversight-core/utils/toast";

const passwordRule =
  "Password must have at least 6 characters and include at least one lowercase letter, one uppercase letter, one numeric and one special character";

type Stage =
  | "enterCode"
  | "checking"
  | "setPassword"
  | "claimed"
  | "alreadyClaimed"
  | "notUsable";

interface ICodeForm {
  code: string;
}

interface IPasswordForm {
  password: string;
  confirmPassword: string;
}

const formatExpiry = (expiresAt: string | null) =>
  expiresAt ? new Date(expiresAt).toLocaleString() : undefined;

const ClaimAccount = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stage, setStage] = useState<Stage>("enterCode");
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<IPreviewClaimResponseDTO>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const linkCodeRead = useRef(false);

  const [previewClaim] = usePreviewClaimMutation();
  const [completeClaim, { isLoading: isCompleting }] =
    useCompleteClaimMutation();

  const codeForm = useForm<ICodeForm>({ defaultValues: { code: "" } });
  const passwordForm = useForm<IPasswordForm>({
    defaultValues: { password: "", confirmPassword: "" },
  });

  const checkCode = async (value: string) => {
    const trimmed = value.trim();
    setCode(trimmed);
    setStage("checking");

    try {
      const result = await previewClaim({ code: trimmed }).unwrap();
      setPreview(result);
      setStage(
        result.availability === "USABLE"
          ? "setPassword"
          : result.availability === "ALREADY_CLAIMED"
            ? "alreadyClaimed"
            : "notUsable",
      );
    } catch (error) {
      showErrorMessage(
        (error as IHttpError)?.message ||
          "The setup code could not be checked. Please try again.",
      );
      codeForm.reset({ code: trimmed });
      setStage("enterCode");
    }
  };

  // The code arrives in the invitation link; take it out of the address so it does not stay in
  // the browser history or get shared along with the page.
  useEffect(() => {
    if (linkCodeRead.current) return;
    linkCodeRead.current = true;

    const linkCode = searchParams.get("code");
    if (linkCode) {
      setSearchParams({}, { replace: true });
      checkCode(linkCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSetPassword = async (data: IPasswordForm) => {
    try {
      await completeClaim({ code, password: data.password }).unwrap();
      setStage("claimed");
    } catch (error) {
      const httpError = error as IHttpError;
      if (httpError?.status === 412) {
        showErrorMessage(passwordRule);
        return;
      }
      // The code may have expired, been replaced or used while the form was open.
      showErrorMessage(
        httpError?.message || "Your account could not be set up.",
      );
      passwordForm.reset();
      checkCode(code);
    }
  };

  const enterAnotherCode = () => {
    codeForm.reset({ code: "" });
    passwordForm.reset();
    setPreview(undefined);
    setStage("enterCode");
  };

  const signInButton = (
    <Row className="mt-4">
      <Col>
        <AppButton
          text="Go to Sign In"
          size="medium"
          onClick={() => navigate(AppRoute.ADMIN_LOGIN)}
        />
      </Col>
    </Row>
  );

  switch (stage) {
    case "checking":
      return (
        <LoginLayout title="Set Up Your Account">
          <Row className="mt-4">
            <Col className="text-center">Checking your setup code...</Col>
          </Row>
        </LoginLayout>
      );

    case "setPassword":
      return (
        <LoginLayout
          title={`Hello ${preview?.firstName ?? "there"}`}
          subTitle={
            <>
              Choose a password to finish setting up your account
              {preview?.maskedDestination
                ? ` for ${preview.maskedDestination}`
                : ""}
              .
              {formatExpiry(preview?.expiresAt ?? null) &&
                ` This setup link expires on ${formatExpiry(
                  preview?.expiresAt ?? null,
                )}.`}
            </>
          }
        >
          <form onSubmit={passwordForm.handleSubmit(onSetPassword)}>
            <Row>
              <Col className="mt-4">
                <AppInput
                  name="password"
                  icon={!showPassword ? "visibility_off" : "visibility"}
                  placeholder="Password"
                  onIconClick={() => setShowPassword(!showPassword)}
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  register={passwordForm.register("password", {
                    required: "Password is required",
                    pattern: { value: passwordRegex, message: passwordRule },
                  })}
                  errors={passwordForm.formState.errors}
                />
              </Col>
            </Row>
            <Row>
              <Col className="mt-4">
                <AppInput
                  name="confirmPassword"
                  icon={!showConfirmPassword ? "visibility_off" : "visibility"}
                  placeholder="Confirm Password"
                  onIconClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  register={passwordForm.register("confirmPassword", {
                    required: "Password confirmation is required",
                    validate: (value, formValues) =>
                      value === formValues.password || "Password doesn't match",
                  })}
                  errors={passwordForm.formState.errors}
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <AppButton
                  text="Set Password"
                  size="medium"
                  type="submit"
                  isLoading={isCompleting}
                />
              </Col>
            </Row>
          </form>
        </LoginLayout>
      );

    case "claimed":
      return (
        <LoginLayout
          title="Your Account Is Ready"
          subTitle={`Sign in with the ${
            preview?.channel === "SMS" ? "mobile number" : "email address"
          } this invitation was sent to${
            preview?.maskedDestination ? ` (${preview.maskedDestination})` : ""
          } and the password you just set.`}
        >
          {signInButton}
        </LoginLayout>
      );

    case "alreadyClaimed":
      return (
        <LoginLayout
          title="Account Already Set Up"
          subTitle="This invitation has already been used to set a password. Sign in with your email address or mobile number. If you have forgotten your password, use Forgot Password on the sign-in page."
        >
          {signInButton}
        </LoginLayout>
      );

    case "notUsable":
      return (
        <LoginLayout
          title="Setup Link Not Valid"
          subTitle="This setup link or code has expired or has been replaced by a newer invitation. Use the most recent invitation you received, or ask your administrator to send a new one."
        >
          <Row className="mt-4">
            <Col>
              <AppButton
                text="Enter Another Code"
                size="medium"
                onClick={enterAnotherCode}
              />
            </Col>
          </Row>
        </LoginLayout>
      );

    default:
      return (
        <LoginLayout
          title="Set Up Your Account"
          subTitle="Enter the setup code from your invitation. It is not a password."
        >
          <form
            onSubmit={codeForm.handleSubmit((data) => checkCode(data.code))}
          >
            <Row>
              <Col className="mt-4">
                <AppInput
                  name="code"
                  placeholder="Setup Code"
                  label="Setup Code"
                  type="text"
                  register={codeForm.register("code", {
                    required: "Setup code is required",
                    validate: (value) =>
                      value.trim() !== "" || "Setup code is required",
                  })}
                  errors={codeForm.formState.errors}
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <AppButton text="Continue" size="medium" type="submit" />
              </Col>
            </Row>
          </form>
        </LoginLayout>
      );
  }
};

export default ClaimAccount;
